/// <reference types="vite/client" />
import { useLingui } from "@lingui/react/macro";
import * as Sentry from "@sentry/tanstackstart-react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	redirect,
	Scripts,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import type * as React from "react";
import { useEffect } from "react";
import { DefaultCatchBoundary } from "~/components/default-catch-boundary";
import { NotFound } from "~/components/not-found";
import { Toaster } from "~/components/ui/sonner";
import { useSession } from "~/hooks/use-session";
import { sessionQueryOptions } from "~/modules/auth/functions/session";
import { seo } from "~/modules/page/seo";
import type { AppContext } from "~/router";
import appCss from "~/styles/app.css?url";

export const Route = Sentry.wrapCreateRootRouteWithSentry(
	createRootRouteWithContext<AppContext>()({
		beforeLoad: async ({ context, location }) => {
			// Support legacy reset password token
			const resetPasswordTokenMatch = location.pathname.match(
				/^\/reset-password\/([^/]+)$/,
			);

			if (resetPasswordTokenMatch) {
				throw redirect({
					to: "/auth/reset-password",
					search: { token: resetPasswordTokenMatch[1] },
				});
			}

			const [session] = await Promise.all([
				context.queryClient.ensureQueryData(sessionQueryOptions()),
			]);

			if (session.user) {
				Sentry.setUser({
					id: session.user.email,
					email: session.user.email,
					username: session.user.name,
				});
			} else {
				Sentry.setUser(null);
			}

			return { session };
		},
		loader(ctx) {
			if (ctx.context.session.user) {
				Sentry.setUser({
					id: ctx.context.session.user.email,
					email: ctx.context.session.user.email,
					username: ctx.context.session.user.name,
				});
			} else {
				Sentry.setUser(null);
			}
		},
		component: RootLayout,
		errorComponent: (props) => {
			useEffect(() => {
				Sentry.captureException(props.error);
			}, [props.error]);
			return (
				<RootDocument>
					<DefaultCatchBoundary {...props} />
				</RootDocument>
			);
		},
		notFoundComponent: () => <NotFound />,
		head: () => ({
			meta: [
				{
					charSet: "utf-8",
				},
				{
					name: "viewport",
					content: "width=device-width, initial-scale=1",
				},
				...seo({
					title: "Podcasts by Azerion",
					description:
						"Easily monetize your podcasts with our advanced monetization platform. Publish your podcast or use your existing podcasts to get started quickly.",
					image: "/og.png",
				}),
			],
			links: [
				{ rel: "stylesheet", href: appCss },
				{
					rel: "apple-touch-icon",
					sizes: "180x180",
					href: "/apple-touch-icon.png",
				},
				{
					rel: "icon",
					type: "image/png",
					sizes: "96x96",
					href: "/favicon-96x96.png",
				},
				{ rel: "manifest", href: "/site.webmanifest", color: "#fffff" },
				{ rel: "icon", href: "/favicon.ico" },
				{ rel: "preconnect", href: "https://rsms.me/" },
				{ rel: "stylesheet", href: "https://rsms.me/inter/inter.css" },
			],
		}),
	}),
);

function RootLayout() {
	return (
		<RootDocument>
			<Outlet />
		</RootDocument>
	);
}

function RootDocument({ children }: { children: React.ReactNode }) {
	const { i18n } = useLingui();
	const { session } = useSession();

	useEffect(() => {
		if (session.user) {
			Sentry.setUser({
				id: session.user.email,
				email: session.user.email,
				username: session.user.name,
			});
		} else {
			Sentry.setUser(null);
		}
	}, [session.user]);

	return (
		<html
			className="antialiased font-sans bg-muted tracking-tight h-full"
			lang={i18n.locale}
		>
			<head>
				<HeadContent />
			</head>
			<body className="h-full overflow-auto">
				{children}
				<Toaster />
				<ReactQueryDevtools position="bottom" buttonPosition="bottom-left" />
				<TanStackRouterDevtools position="bottom-left" />
				<Scripts />
			</body>
		</html>
	);
}
